import { fetchAuthSession } from 'aws-amplify/auth';

// admin-api (docker/admin-api) is a separate origin in production
// (admin-api.tippingiq.com vs the SPA's admin.tippingiq.com), so it needs an
// explicit base URL there. Locally VITE_ADMIN_API_URL is unset and requests fall
// back to the '/api' dev proxy configured in vite.config.ts.
const API_BASE_URL = import.meta.env.VITE_ADMIN_API_URL || '/api';

// The brand this console is acting as (multi-tenant merge M3d), sent as
// X-Brand-Key on every request. admin-api validates it against the admin's
// admin_brand_access and fails closed. Set once by BrandProvider after /me
// resolves; unset (no header) only for that initial /me call, where the
// server falls back to its own default brand.
let activeBrandKey: string | null = null;

export function setActiveBrandKey(brandKey: string | null): void {
  activeBrandKey = brandKey;
}

export function getActiveBrandKey(): string | null {
  return activeBrandKey;
}

function withBrandHeader(headers: Headers): Headers {
  if (activeBrandKey) headers.set('X-Brand-Key', activeBrandKey);
  return headers;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Typed fetch wrapper for admin-api: attaches the Cognito ID token and parses JSON. */
export async function apiClient<T>(path: string, options: RequestInit = {}): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  const headers = withBrandHeader(new Headers(options.headers));
  // JSON unless the caller sent something else (e.g. a raw image upload).
  if (!headers.has('Content-Type')) headers.set('Content-Type', 'application/json');
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, { ...options, headers });

  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, body || response.statusText);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

/**
 * Downloads a non-JSON response (e.g. a CSV export) as a file. A plain
 * <a href> to the API URL won't carry the Cognito bearer token this app
 * authenticates with (unlike the old admin's cookie-based NextAuth session),
 * so the request has to go through fetch with the same auth header apiClient
 * attaches, then be handed to the browser as a Blob.
 */
export async function downloadFile(path: string, filename: string): Promise<void> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();

  const headers = withBrandHeader(new Headers());
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(`${API_BASE_URL}${path}`, { headers });
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw new ApiError(response.status, body || response.statusText);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
